import axios from "axios"

import type {
  LocalAiLeadAllowance,
  LocalAiLeadBudgetRequest,
  LocalAiLeadRequestErrorCode,
} from "@/api/local-ai-lead-api"

// Non-component helpers for the lead approvals card. Kept out of the .tsx so
// the component files export components only (react-refresh rule).

/** A lead endpoint's structured failure, as the card needs to render it. */
export type LeadRequestError = {
  /** Stable machine code, when the body carried one. */
  code?: LocalAiLeadRequestErrorCode
  /** The backend's human sentence — used as the fallback toast. */
  message?: string
  /** Only on `lead_allowance_exceeded`. */
  allowanceSek?: number
  usedSek?: number
  requestedSek?: number
}

const LEAD_REQUEST_ERROR_CODES: readonly string[] = [
  "fair_use_not_configured",
  "invalid_request",
  "request_not_found",
  "request_not_pending",
  "month_locked",
  "month_in_future",
  "lead_not_mapped",
  "lead_user_not_assigned",
  "lead_self_approval",
  "lead_allowance_exceeded",
]

function readNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

/**
 * Pull the structured `{error, code, ...}` body off a failed approve/deny.
 *
 * Every lead endpoint answers with both a human `error` sentence and a stable
 * `code`; the card localizes the code when it knows it and falls back to the
 * sentence when it does not, so a backend that grows a new refusal still says
 * something true rather than "Something went wrong".
 */
export function readLeadRequestError(error: unknown): LeadRequestError {
  if (!axios.isAxiosError(error)) return {}
  const data: unknown = error.response?.data
  if (!data || typeof data !== "object") return {}
  const body = data as Record<string, unknown>
  const rawCode = body.code
  return {
    code:
      typeof rawCode === "string" && LEAD_REQUEST_ERROR_CODES.includes(rawCode)
        ? (rawCode as LocalAiLeadRequestErrorCode)
        : undefined,
    message: typeof body.error === "string" ? body.error : undefined,
    allowanceSek: readNumber(body.allowance_sek),
    usedSek: readNumber(body.used_sek),
    requestedSek: readNumber(body.requested_sek),
  }
}

/** i18n leaf under `localAiLead.requests.errors.*` for a known code. */
export function leadRequestErrorKey(code: LocalAiLeadRequestErrorCode | undefined): string | null {
  if (!code) return null
  return `localAiLead.requests.errors.${code}`
}

/** Display name for a request row: profile name, then e-mail, then a short oid. */
export function leadRequestDisplayName(row: LocalAiLeadBudgetRequest): string {
  const name = row.profile?.name?.trim()
  if (name) return name
  const email = (row.profile?.email ?? row.email)?.trim()
  if (email) return email
  return `${row.user_id.slice(0, 8)}...${row.user_id.slice(-4)}`
}

/** Secondary line: department first — an oid never answers "which person is this". */
export function leadRequestSecondaryLine(row: LocalAiLeadBudgetRequest): string | undefined {
  const department = row.profile?.department?.trim()
  if (department) return department
  const email = (row.profile?.email ?? row.email)?.trim()
  return email || undefined
}

/**
 * How many blocks this lead may still approve, clamped to at least 1 for the
 * input's own bounds.
 *
 * The backend's `max_blocks` is authoritative and already accounts for what
 * the lead has spent this month. Zero means "nothing left", which the card
 * renders as a disabled approve action rather than as a 1-block input that is
 * guaranteed to 409.
 */
export function leadApprovableBlocks(allowance: LocalAiLeadAllowance | undefined): number {
  if (!allowance) return 0
  const max = allowance.max_blocks
  return typeof max === "number" && Number.isFinite(max) && max > 0 ? Math.floor(max) : 0
}

/** SEK with two decimals, locale-formatted; "-" for a missing/non-finite value. */
export function formatLeadSek(value: number | null | undefined, locale: string): string {
  if (value == null || !Number.isFinite(value)) return "-"
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  } catch {
    return value.toFixed(2)
  }
}
